import {
  type AssessmentSlideOverCandidate,
  useAssessmentSlideOver,
} from "@components/campaigns/AssessmentSlideOver";
import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { OpenFullProfileLink } from "@components/candidates/OpenFullProfileLink";
import { ExportButton } from "@components/exports/ExportButton";
import { shortAnchorFor } from "@lib/constants/scoringScales";
import {
  EXPORT_CANDIDATE_SCORING_MATRIX_TO_GOOGLE_DOCS,
  EXPORT_CANDIDATE_SCORING_MATRIX_TO_GOOGLE_SHEETS,
  SCORE_CANDIDATE_SCORING_MATRIX_CELL,
  TOGGLE_CANDIDATE_SCORING_MATRIX_LOCK,
  UPDATE_ASSESSMENT_SCORES,
} from "@lib/graphql/mutations";
import { CANDIDATE_SCORING_MATRIX_PAGE_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpDown,
  Loader2,
  Lock,
  Sparkles,
  Unlock,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery } from "urql";
import { MatrixCellEditor } from "./MatrixCellEditor";
import { MatrixViewToggle } from "./MatrixViewToggle";
import { UpdateAssessmentScoresDialog } from "./UpdateAssessmentScoresDialog";

interface CellData {
  capabilityDimensionId: string;
  competencyScoreId: string | null;
  score: number | null;
  evidence: string | null;
  anchorLabel: string | null;
  idealScore: number | null;
  flagsThreshold: boolean;
  stale: boolean;
  staleReasons: string[];
  currentBandBody: string | null;
}

interface RowData {
  campaignCandidateId: string;
  candidateId: string;
  candidateName: string;
  referenceNumber: number;
  overallScore: number | null;
  thresholdPassing: boolean | null;
  assessmentVersionId: string | null;
  cells: CellData[];
}

interface DimensionData {
  id: string;
  name: string;
  idealScore: number | null;
  critical: boolean;
}

interface IneligibleCandidate {
  campaignCandidateId: string;
  candidateId: string;
  name: string;
}

interface MatrixData {
  id: string;
  locked: boolean;
  state: string;
  currentVersion: {
    id: string;
    versionNumber: number;
    generationState: string;
    generationError: string | null;
    roleBlueprintVersionNumber: number | null;
    effectiveFrom: string;
  } | null;
  dimensions: DimensionData[];
  rows: RowData[];
  ineligibleCandidatesForScoring: IneligibleCandidate[];
}

interface AssessmentMatrixSummary {
  id: string;
  locked: boolean;
  outOfSync: boolean;
  currentVersion: {
    id: string;
    versionNumber: number;
    roleBlueprintVersionNumber: number | null;
  } | null;
}

interface CampaignData {
  id: string;
  title: string;
  roleBlueprint: {
    id: string;
    locked: boolean;
    currentVersion: { id: string; versionNumber: number } | null;
    capabilityDimensions: {
      id: string;
      continuityId: string;
      name: string;
      idealScore: number | null;
    }[];
  } | null;
  assessmentMatrix: AssessmentMatrixSummary | null;
  campaignCandidates: AssessmentSlideOverCandidate[];
  candidateScoringMatrix: MatrixData | null;
}

type SortKey = "candidate" | "overall" | string; // dimension id when sorting by a column
type SortDirection = "asc" | "desc";
type ThresholdFilter = "all" | "passing" | "failing" | "unscored" | "stale";

interface ActiveCell {
  rowId: string;
  dimensionId: string;
  candidateName: string;
  dimensionName: string;
  idealScore: number | null;
  initialScore: number | null;
  initialEvidence: string | null;
  currentBandBody: string | null;
  staleReasons: string[];
}

export function CandidateScoringMatrixPage() {
  const { id } = useParams();
  const campaignId = id ?? "";

  const [{ data, fetching, error }, reexecute] = useQuery<{ campaign: CampaignData }>({
    query: CANDIDATE_SCORING_MATRIX_PAGE_QUERY,
    variables: { id: campaignId },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const [, scoreCell] = useMutation(SCORE_CANDIDATE_SCORING_MATRIX_CELL);
  const [{ fetching: toggling }, toggleLock] = useMutation(TOGGLE_CANDIDATE_SCORING_MATRIX_LOCK);
  const [{ fetching: regenerating }, updateAssessmentScores] =
    useMutation(UPDATE_ASSESSMENT_SCORES);
  const [, exportToDocs] = useMutation(EXPORT_CANDIDATE_SCORING_MATRIX_TO_GOOGLE_DOCS);
  const [, exportToSheets] = useMutation(EXPORT_CANDIDATE_SCORING_MATRIX_TO_GOOGLE_SHEETS);

  const [sortKey, setSortKey] = useState<SortKey>("overall");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const [thresholdFilter, setThresholdFilter] = useState<ThresholdFilter>("all");
  const [showEvidence, setShowEvidence] = useState(false);
  const [activeCell, setActiveCell] = useState<ActiveCell | null>(null);
  const [savingCell, setSavingCell] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [updateScoresDialogOpen, setUpdateScoresDialogOpen] = useState(false);
  const [manualEditsDialogOpen, setManualEditsDialogOpen] = useState(false);

  const campaign = data?.campaign;
  const matrix = campaign?.candidateScoringMatrix ?? null;
  const assessmentMatrix = campaign?.assessmentMatrix ?? null;
  const assessmentMatrixOutOfSync = assessmentMatrix?.outOfSync ?? false;
  const assessmentMatrixLocked = assessmentMatrix?.locked ?? false;
  const blueprintLocked = campaign?.roleBlueprint?.locked ?? false;
  const dimensions = matrix?.dimensions ?? [];
  const rows = matrix?.rows ?? [];
  const ineligibleForScoring = matrix?.ineligibleCandidatesForScoring ?? [];
  const generating = matrix?.currentVersion?.generationState === "generating";

  const { open: openAssessment, slideOverElement: assessmentSlideOver } = useAssessmentSlideOver({
    candidates: campaign?.campaignCandidates ?? [],
    dimensions: campaign?.roleBlueprint?.capabilityDimensions ?? [],
  });

  const filteredAndSorted = useMemo(() => {
    const filtered = rows.filter((r) => {
      if (thresholdFilter === "all") return true;
      if (thresholdFilter === "passing") return r.thresholdPassing === true;
      if (thresholdFilter === "failing") return r.thresholdPassing === false;
      if (thresholdFilter === "stale") return r.cells.some((c) => c.stale);
      return r.thresholdPassing === null;
    });

    const cmp = (a: RowData, b: RowData) => {
      const sign = sortDir === "asc" ? 1 : -1;
      if (sortKey === "candidate") {
        return sign * a.candidateName.localeCompare(b.candidateName);
      }
      if (sortKey === "overall") {
        return sign * cmpNullsLast(a.overallScore, b.overallScore);
      }
      const aCell = a.cells.find((c) => c.capabilityDimensionId === sortKey);
      const bCell = b.cells.find((c) => c.capabilityDimensionId === sortKey);
      return sign * cmpNullsLast(aCell?.score ?? null, bCell?.score ?? null);
    };

    return [...filtered].sort(cmp);
  }, [rows, sortKey, sortDir, thresholdFilter]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "candidate" ? "asc" : "desc");
    }
  };

  const openCell = (row: RowData, cell: CellData, dimension: DimensionData) => {
    if (matrix?.locked) return;
    setActiveCell({
      rowId: row.campaignCandidateId,
      dimensionId: cell.capabilityDimensionId,
      candidateName: row.candidateName,
      dimensionName: dimension.name,
      idealScore: dimension.idealScore,
      initialScore: cell.score,
      initialEvidence: cell.evidence,
      currentBandBody: cell.currentBandBody,
      staleReasons: cell.staleReasons,
    });
  };

  const handleSaveCell = async (score: number, evidence: string) => {
    if (!activeCell) return;
    setSavingCell(true);
    setBannerError(null);
    const result = await scoreCell({
      input: {
        campaignCandidateId: activeCell.rowId,
        capabilityDimensionId: activeCell.dimensionId,
        score,
        evidence,
      },
    });
    setSavingCell(false);
    const errors = result.data?.scoreCandidateScoringMatrixCell?.errors ?? [];
    if (errors.length) {
      setBannerError(errors.join(", "));
      return;
    }
    setActiveCell(null);
    refresh();
  };

  const handleToggleLock = async () => {
    setBannerError(null);
    await toggleLock({ input: { campaignId } });
    refresh();
  };

  const runUpdateAssessmentScores = async (
    forceAssessmentMatrix: boolean,
    confirmOverwrite = false,
  ) => {
    setBannerError(null);
    const result = await updateAssessmentScores({
      input: { campaignId, forceAssessmentMatrix, confirmOverwrite },
    });
    const payload = result.data?.updateAssessmentScores;
    const errors = payload?.errors ?? [];
    if (errors.length) {
      setBannerError(errors.join(", "));
      return;
    }
    // A candidate's assessment was manually edited — rescoring would overwrite
    // it. Surface the confirm-overwrite dialog rather than silently doing
    // nothing; confirming re-runs with confirmOverwrite: true.
    if (payload?.status === "manual_edits_present") {
      setManualEditsDialogOpen(true);
      return;
    }
    refresh();
  };

  const handleUpdateScores = async () => {
    if (!blueprintLocked) {
      setBannerError("Lock the role blueprint before updating assessment scores.");
      return;
    }
    if (assessmentMatrixOutOfSync) {
      setUpdateScoresDialogOpen(true);
      return;
    }
    await runUpdateAssessmentScores(false);
  };

  const handleConfirmUpdateScoresDialog = async () => {
    await runUpdateAssessmentScores(true);
    setUpdateScoresDialogOpen(false);
  };

  const handleConfirmManualEditsDialog = async () => {
    setManualEditsDialogOpen(false);
    await runUpdateAssessmentScores(false, true);
  };

  const handleExportToDocsStart = async () => {
    const result = await exportToDocs({
      input: { campaignId, includeEvidence: showEvidence },
    });
    const payload = result.data?.exportCandidateScoringMatrixToGoogleDocs;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  };

  const handleExportToSheetsStart = async () => {
    const result = await exportToSheets({
      input: { campaignId, includeEvidence: showEvidence },
    });
    const payload = result.data?.exportCandidateScoringMatrixToGoogleSheets;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  };

  // ESC closes the editor; click-elsewhere closes via backdrop click handler
  // on the table cell wrapper (handled implicitly by re-rendering).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveCell(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Always render the back link first, even on loading / error / empty
  // states — the user must always have a way out of this page.
  const backLink = (
    <Link
      to={`/campaigns/${campaignId}/run/pipeline/scoring`}
      className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary"
    >
      <ArrowLeft size={14} />
      Back to scoring
    </Link>
  );

  if (fetching && !campaign) {
    return (
      <div className="space-y-4 p-6">
        {backLink}
        <p className="text-sm text-text-muted">Loading assessment matrix…</p>
      </div>
    );
  }
  if (error || !campaign) {
    return (
      <div className="space-y-4 p-6">
        {backLink}
        <p className="text-sm text-error">
          Could not load campaign. {error?.message ?? "No data returned."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        {backLink}
        <MatrixViewToggle campaignId={campaignId} />
      </div>

      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-text-primary">
              Candidate Scoring Matrix — {campaign.title}
            </h1>
            <p className="mt-1 text-xs text-text-faint" data-testid="matrix-version-meta">
              {matrix?.currentVersion ? (
                <>
                  v{matrix.currentVersion.versionNumber}
                  {matrix.currentVersion.roleBlueprintVersionNumber != null &&
                    ` · pinned to blueprint v${matrix.currentVersion.roleBlueprintVersionNumber}`}
                </>
              ) : (
                "Not yet generated"
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                matrix?.locked ? "bg-warning-bg text-warning" : "bg-card-hover text-text-muted"
              }`}
              data-testid="matrix-state-badge"
            >
              {matrix?.locked ? "locked" : "draft"}
            </span>
            <button
              type="button"
              onClick={handleUpdateScores}
              disabled={!blueprintLocked || generating || regenerating || matrix?.locked}
              title={
                !blueprintLocked
                  ? "Lock the role blueprint first"
                  : matrix?.locked
                    ? "Unlock the candidate scoring matrix to update scores"
                    : generating
                      ? "Update already in progress"
                      : assessmentMatrixOutOfSync
                        ? "Update the assessment matrix and rescore every candidate"
                        : "Rescore every candidate against the assessment matrix"
              }
              data-testid="update-assessment-scores-button"
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {generating || regenerating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Sparkles size={14} />
              )}
              Update assessment scores
            </button>
            <button
              type="button"
              onClick={handleToggleLock}
              disabled={toggling}
              className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium hover:bg-card-hover ${
                matrix?.locked ? "text-highlight" : "text-text-secondary"
              }`}
            >
              {matrix?.locked ? <Lock size={14} /> : <Unlock size={14} />}
              {matrix?.locked ? "Unlock" : "Lock"}
            </button>
            {rows.length > 0 && (
              <>
                <ExportButton
                  label="Export to Google Docs"
                  onStart={handleExportToDocsStart}
                  className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
                />
                <ExportButton
                  label="Export to Google Sheets"
                  onStart={handleExportToSheetsStart}
                  className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
                />
              </>
            )}
          </div>
        </div>

        {bannerError && (
          <div className="mt-3 flex items-start gap-2 rounded border border-error/30 bg-error-bg p-2 text-sm text-error">
            <AlertTriangle size={14} className="mt-0.5" />
            <span>{bannerError}</span>
          </div>
        )}

        {assessmentMatrixOutOfSync && (
          <div
            className="mt-3 flex items-start gap-2 rounded border border-warning/30 bg-warning-bg p-2 text-sm text-warning"
            data-testid="assessment-matrix-stale-banner"
          >
            <AlertTriangle size={14} className="mt-0.5" />
            <span>
              Assessment matrix is out of date for the current role blueprint. Updating scores will
              refresh it first
              {assessmentMatrixLocked ? "; the matrix's lock will be preserved." : "."}
            </span>
          </div>
        )}

        {ineligibleForScoring.length > 0 && (
          <div
            className="mt-3 rounded border border-warning/30 bg-warning-bg p-2 text-sm text-warning"
            data-testid="ineligible-candidates-banner"
          >
            <div className="flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                <p>
                  {ineligibleForScoring.length === 1
                    ? "The following candidate needs"
                    : "The following candidates need"}{" "}
                  either a CV, LinkedIn profile data, interview notes, or free-text notes before
                  they can be scored:
                </p>
                <ul className="ml-4 mt-1 list-disc">
                  {ineligibleForScoring.map((c) => (
                    <li key={c.campaignCandidateId}>{c.name}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="threshold-filter">
          <span className="text-xs text-text-faint">Threshold:</span>
          {(
            [
              ["all", "all"],
              ["passing", "Meets bar"],
              ["failing", "Below bar"],
              ["unscored", "unscored"],
              ["stale", "stale"],
            ] as [ThresholdFilter, string][]
          ).map(([opt, label]) => (
            <button
              key={opt}
              type="button"
              onClick={() => setThresholdFilter(opt)}
              className={`rounded-full px-2 py-0.5 text-xs ${
                thresholdFilter === opt
                  ? "bg-accent text-white"
                  : "bg-card-alt text-text-secondary hover:bg-card-hover"
              }`}
            >
              {label}
            </button>
          ))}
          <span className="ml-2 h-3 w-px bg-border" aria-hidden />
          <button
            type="button"
            onClick={() => setShowEvidence((v) => !v)}
            data-testid="show-evidence-toggle"
            aria-pressed={showEvidence}
            className={`rounded-full px-2 py-0.5 text-xs ${
              showEvidence
                ? "bg-accent text-white"
                : "bg-card-alt text-text-secondary hover:bg-card-hover"
            }`}
          >
            {showEvidence ? "Hide evidence" : "Show evidence"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg bg-card shadow-sm ring-1 ring-ring">
        {dimensions.length === 0 ? (
          <p className="p-6 text-sm text-text-muted">
            No capability dimensions defined yet. Add some in the Role Blueprint stage to see the
            matrix grid.
          </p>
        ) : filteredAndSorted.length === 0 ? (
          <p className="p-6 text-sm text-text-muted">
            No candidates match the current filter. Adjust filters or add an assessment.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-card-alt text-xs text-text-secondary">
                <th
                  className="sticky left-0 z-10 cursor-pointer bg-card-alt p-2 text-left"
                  onClick={() => handleSort("candidate")}
                >
                  <span className="inline-flex items-center gap-1">
                    Candidate
                    <ArrowUpDown size={10} className="text-text-faint" />
                  </span>
                </th>
                {dimensions.map((d) => (
                  <th
                    key={d.id}
                    className="cursor-pointer p-2 text-left"
                    onClick={() => handleSort(d.id)}
                  >
                    <div className="flex items-center gap-1">
                      <span>{d.name}</span>
                      {d.idealScore != null && (
                        <span className="text-text-faint">·{d.idealScore}/10</span>
                      )}
                      {d.critical && (
                        <span className="rounded bg-error-bg px-1 text-[10px] font-medium uppercase tracking-wide text-error">
                          critical
                        </span>
                      )}
                      <ArrowUpDown size={10} className="text-text-faint" />
                    </div>
                  </th>
                ))}
                <th className="cursor-pointer p-2 text-left" onClick={() => handleSort("overall")}>
                  <span className="inline-flex items-center gap-1">
                    Overall
                    <ArrowUpDown size={10} className="text-text-faint" />
                  </span>
                </th>
                <th className="p-2 text-left">Threshold</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.map((row) => (
                <tr
                  key={row.campaignCandidateId}
                  className="border-b border-border last:border-0"
                  data-testid={`matrix-row-${row.campaignCandidateId}`}
                >
                  <td
                    className={`sticky left-0 z-10 bg-card p-2 font-medium text-text-primary ${
                      showEvidence ? "align-top" : ""
                    }`}
                  >
                    <Link
                      to={`/campaigns/${campaignId}/candidates/${row.campaignCandidateId}`}
                      className="hover:text-accent hover:underline"
                    >
                      {row.candidateName}
                    </Link>
                    <CandidateReferenceBadge referenceNumber={row.referenceNumber} />
                    <div className="mt-1">
                      <OpenFullProfileLink candidateId={row.candidateId} />
                    </div>
                  </td>
                  {dimensions.map((d) => {
                    const cell = row.cells.find((c) => c.capabilityDimensionId === d.id);
                    if (!cell)
                      return (
                        <td
                          key={d.id}
                          className={`p-2 text-text-faint ${showEvidence ? "align-top" : ""}`}
                        >
                          —
                        </td>
                      );
                    return (
                      <td
                        key={d.id}
                        className={`p-2 align-top ${showEvidence ? "min-w-[14rem]" : ""}`}
                        data-testid={`cell-${row.campaignCandidateId}-${d.id}`}
                      >
                        <span
                          className={`relative inline-flex items-center ${
                            cell.stale ? "rounded ring-2 ring-highlight ring-offset-1" : ""
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => openCell(row, cell, d)}
                            disabled={matrix?.locked ?? false}
                            title={
                              cell.stale
                                ? staleTooltip(cell.staleReasons)
                                : (shortAnchorFor(cell.score) ?? "Click to score")
                            }
                            className={`inline-flex h-7 min-w-[28px] items-center justify-center rounded text-sm font-medium transition-colors ${
                              cell.score == null
                                ? "border border-dashed border-border text-text-faint hover:bg-decision"
                                : cell.flagsThreshold
                                  ? "bg-error-bg text-error hover:bg-error/15"
                                  : "bg-card-alt text-text-primary hover:bg-decision"
                            } disabled:cursor-not-allowed disabled:opacity-70`}
                          >
                            {cell.score ?? "—"}
                          </button>
                          {cell.stale && (
                            <span
                              role="img"
                              aria-label="Score may be stale"
                              className="pointer-events-none absolute -right-2.5 -top-2.5 inline-flex items-center justify-center rounded-full bg-highlight text-white shadow ring-2 ring-card"
                              style={{ width: 14, height: 14 }}
                              data-testid={`stale-badge-${row.campaignCandidateId}-${d.id}`}
                            >
                              <AlertTriangle size={9} />
                            </span>
                          )}
                        </span>
                        {showEvidence &&
                          (cell.evidence ? (
                            <p
                              className="mt-1 max-w-xs whitespace-pre-wrap text-xs text-text-secondary"
                              data-testid={`cell-evidence-${row.campaignCandidateId}-${d.id}`}
                            >
                              {cell.evidence}
                            </p>
                          ) : (
                            <p className="mt-1 text-xs italic text-text-faint">No evidence yet.</p>
                          ))}
                      </td>
                    );
                  })}
                  <td
                    className={`p-2 text-sm font-semibold text-text-primary tabular-nums ${
                      showEvidence ? "align-top" : ""
                    }`}
                  >
                    {row.overallScore != null && row.assessmentVersionId ? (
                      <button
                        type="button"
                        onClick={() => openAssessment(row.campaignCandidateId)}
                        title="View full assessment"
                        className="rounded px-1.5 py-0.5 hover:bg-card-hover hover:text-accent"
                        data-testid={`open-assessment-${row.campaignCandidateId}`}
                      >
                        {row.overallScore.toFixed(1)}
                      </button>
                    ) : row.overallScore != null ? (
                      row.overallScore.toFixed(1)
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className={`p-2 ${showEvidence ? "align-top" : ""}`}>
                    {row.thresholdPassing === true ? (
                      <span className="text-xs font-medium text-success">Meets bar</span>
                    ) : row.thresholdPassing === false ? (
                      <span className="text-xs font-medium text-error">Below bar</span>
                    ) : (
                      <span className="text-xs text-text-faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {activeCell && (
        <MatrixCellEditor
          initialScore={activeCell.initialScore}
          initialEvidence={activeCell.initialEvidence}
          saving={savingCell}
          onSave={handleSaveCell}
          onCancel={() => setActiveCell(null)}
          candidateName={activeCell.candidateName}
          dimensionName={activeCell.dimensionName}
          idealScore={activeCell.idealScore}
          campaignCandidateId={activeCell.rowId}
          currentBandBody={activeCell.currentBandBody}
          staleReasons={activeCell.staleReasons}
        />
      )}

      <UpdateAssessmentScoresDialog
        open={updateScoresDialogOpen}
        matrixLocked={assessmentMatrixLocked}
        pinnedBlueprintVersion={
          assessmentMatrix?.currentVersion?.roleBlueprintVersionNumber ?? null
        }
        currentBlueprintVersion={campaign?.roleBlueprint?.currentVersion?.versionNumber ?? null}
        onClose={() => setUpdateScoresDialogOpen(false)}
        onConfirm={handleConfirmUpdateScoresDialog}
      />
      <UpdateAssessmentScoresDialog
        open={manualEditsDialogOpen}
        variant="manual_edits"
        matrixLocked={false}
        pinnedBlueprintVersion={null}
        currentBlueprintVersion={null}
        onClose={() => setManualEditsDialogOpen(false)}
        onConfirm={handleConfirmManualEditsDialog}
      />
      {assessmentSlideOver}
    </div>
  );
}

function cmpNullsLast(a: number | null, b: number | null) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
}

function staleTooltip(reasons: string[]): string {
  const parts = reasons.map((r) => {
    if (r === "ideal_score_changed") return "Ideal score has changed";
    if (r === "dimension_changed") return "Dimension definition has changed";
    if (r === "rubric_changed") return "Matrix criteria have changed";
    return r;
  });
  return `${parts.join("; ")} — re-evaluate.`;
}
